import React, { useMemo, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { ResponsiveContainer, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatCurrency } from '../../../../utils/formatters';

const currency = (n) => formatCurrency(Number(n || 0));

// Compute a blended multiple based on company MOR data.
// Without fee/non-recurring breakdown, we assume 70% recurring, 30% non-recurring.
const defaultMultipleForType = (businessType) => {
  const t = String(businessType || '').toLowerCase();
  if (t.includes('wealth')) return 2.0;
  if (t.includes('p_and_c') || t.includes('p&c')) return 1.0;
  return 2.0;
};

const computeBlendedMultiple = (company) => {
  if (!company) return defaultMultipleForType();
  const rec = Number(company?.rj_mor_recurring_avg ?? NaN);
  // Some schemas may not expose non-recurring avg; fallback to recurring only
  const nonrecRaw = company?.rj_mor_non_recurring_avg;
  const hasNonRec = isFinite(Number(nonrecRaw));
  if (!isFinite(rec)) return defaultMultipleForType(company?.business_type);
  if (!hasNonRec) return rec; // fallback: use recurring as proxy
  const nonrec = Number(nonrecRaw);
  const weightRecurring = 0.7;
  const weightNonRecurring = 0.3;
  return rec * weightRecurring + nonrec * weightNonRecurring;
};

const readNumber = (obj, key) => {
  const v = obj ? obj[key] : null;
  if (v == null) return 0;
  const n = (typeof v === 'string') ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const buildSeries = (user, company) => {
  const multiple = computeBlendedMultiple(company);
  const nowYear = new Date().getFullYear();
  const years = [
    { offset: 4, field: 'year_5_gross' },
    { offset: 3, field: 'year_4_gross' },
    { offset: 2, field: 'year_3_gross' },
    { offset: 1, field: 'year_2_gross' },
    { offset: 0, field: 'year_1_gross' },
  ];
  const pts = years.map(({ offset, field }) => {
    const revenue = readNumber(user, field);
    const year = nowYear - offset;
    return { year, revenue, valuation: multiple != null ? revenue * multiple : null };
  }).sort((a, b) => a.year - b.year);
  return { points: pts, multiple };
};

const GET_VALUATION_TREND_DATA = gql`
  query GetValuationTrendData {
    getCurrentUser {
      id
      __typename
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
      year_6_gross
      company { __typename rj_mor_recurring_avg business_type }
    }
  }
`;

const EmptyState = ({ title, subtext, primaryCta, primaryHref, secondaryCta, secondaryHref }) => (
  <div className="h-full flex items-center justify-center text-center">
    <div>
      <h3 className="text-succession-text-primary m-0 p-0">{title}</h3>
      {subtext && <p className="text-succession-text-secondary text-sm max-w-xl mx-auto">{subtext}</p>}
      <div className="mt-3 flex items-center justify-center gap-3">
        {primaryCta && (
          <a className="inline-flex items-center px-3 py-1.5 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm" href={primaryHref || '#'}>{primaryCta}</a>
        )}
        {secondaryCta && (
          <a className="inline-flex items-center px-3 py-1.5 rounded-md border border-succession-border text-succession-text-primary hover:bg-succession-hover-bg text-sm" href={secondaryHref || '#'}>{secondaryCta}</a>
        )}
      </div>
    </div>
  </div>
);

const ValuationTrend = ({ user, company, ariaLabel }) => {
  const intl = useIntl();
  const resolvedAriaLabel = ariaLabel || intl.formatMessage({ id: 'succession.valuation_trend.title' });
  // Fetch missing fields if not provided by parent
  const { data, loading, error } = useQuery(GET_VALUATION_TREND_DATA, { fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first', errorPolicy: 'all' });
  const [manualUser, setManualUser] = useState(null);
  const qUser = data?.getCurrentUser || {};
  const storeUser = (() => { try { return (typeof document !== 'undefined' && document.store?.getState?.()?.user) || {}; } catch(_) { return {}; } })();
  const mergedUser = { ...(user || {}), ...storeUser, ...(manualUser || {}), ...qUser };
  const mergedCompany = (company || mergedUser?.company || {});
  const { points, multiple } = useMemo(() => buildSeries(mergedUser, mergedCompany), [mergedUser, mergedCompany]);
  const nonZeroCount = Array.isArray(points) ? points.filter((p) => Number(p.revenue) > 0).length : 0;

  // Debug logging to assist diagnosis in the field
  try {
    // Only log when data changes to avoid noise
    // eslint-disable-next-line no-console
    console.log('[ValuationTrend] debug', {
      gqlUser: qUser,
      storeUser,
      propsUser: user,
      mergedCompany,
      raw: {
        y1: (qUser?.year_1_gross ?? storeUser?.year_1_gross ?? user?.year_1_gross),
        y2: (qUser?.year_2_gross ?? storeUser?.year_2_gross ?? user?.year_2_gross),
        y3: (qUser?.year_3_gross ?? storeUser?.year_3_gross ?? user?.year_3_gross),
        y4: (qUser?.year_4_gross ?? storeUser?.year_4_gross ?? user?.year_4_gross),
        y5: (qUser?.year_5_gross ?? storeUser?.year_5_gross ?? user?.year_5_gross),
      },
      error: error ? { message: error.message, graphQLErrors: error.graphQLErrors } : null,
      multiple,
      points,
      nonZeroCount,
    });
  } catch (_) {}

  // Manual fallback fetch if GraphQL client returns no user
  useEffect(() => {
    if (!loading && (!qUser || Object.keys(qUser).length === 0) && !manualUser) {
      (async () => {
        try {
          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
          const res = await fetch('/graphql', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
            credentials: 'same-origin',
            body: JSON.stringify({ query: `query VTrend { getCurrentUser { id year_1_gross year_2_gross year_3_gross year_4_gross year_5_gross year_6_gross company { rj_mor_recurring_avg business_type } } }` }),
          });
          const json = await res.json();
          const u = json?.data?.getCurrentUser;
          if (u) {
            setManualUser(u);
            try { /* eslint-disable no-console */ console.log('[ValuationTrend] manual fetch used', u); /* eslint-enable no-console */ } catch (_) {}
          } else {
            try { /* eslint-disable no-console */ console.log('[ValuationTrend] manual fetch empty', json); /* eslint-enable no-console */ } catch (_) {}
          }
        } catch (e) {
          try { /* eslint-disable no-console */ console.error('[ValuationTrend] manual fetch error', e); /* eslint-enable no-console */ } catch (_) {}
        }
      })();
    }
  }, [loading, qUser, manualUser]);

  useEffect(() => {
    try { if (window.ahoy?.track) window.ahoy.track('valuation_trend_rendered'); } catch (_) {}
  }, []);

  // Suppress noisy ResizeObserver loop errors that can occur with responsive charts during rapid layout changes
  useEffect(() => {
    const suppress = (e) => {
      const msg = e?.message || '';
      if (msg.includes('ResizeObserver loop completed with undelivered notifications') || msg.includes('ResizeObserver loop limit exceeded')) {
        try { e.stopImmediatePropagation(); } catch (_) {}
      }
    };
    window.addEventListener('error', suppress);
    window.addEventListener('unhandledrejection', suppress);
    return () => {
      window.removeEventListener('error', suppress);
      window.removeEventListener('unhandledrejection', suppress);
    };
  }, []);

  if (loading && (!Array.isArray(points) || points.length === 0)) {
    try { /* eslint-disable no-console */ console.log('[ValuationTrend] loading skeleton'); /* eslint-enable no-console */ } catch (_) {}
    return (
      <div className="w-full h-full animate-pulse bg-transparent rounded-md" aria-hidden="true" />
    );
  }

  if (!Array.isArray(points) || nonZeroCount < 3) {
    try { if (window.ahoy?.track) window.ahoy.track('valuation_trend_empty', { count: nonZeroCount }); } catch (_) {}
    try { /* eslint-disable no-console */ console.log('[ValuationTrend] empty-state', { nonZeroCount, points, multiple }); /* eslint-enable no-console */ } catch (_) {}
    return (
      <div className="h-full min-h-[200px] flex items-center justify-center">
        <EmptyState
          title={intl.formatMessage({ id: 'succession.valuation_trend.empty_title' })}
          subtext={intl.formatMessage({ id: 'succession.valuation_trend.empty_body' })}
          primaryCta={intl.formatMessage({ id: 'succession.valuation_trend.add_revenue' })}
          primaryHref="/succession/profile"
          secondaryCta={intl.formatMessage({ id: 'succession.valuation_trend.learn_multiples' })}
          secondaryHref="/succession/education"
        />
      </div>
    );
  }

  const chartData = points.filter((p) => p.valuation != null).map((p) => ({ name: String(p.year), valuation: Math.round(p.valuation), revenue: p.revenue }));

  // Simple peer benchmark placeholder: TODO replace with cohort benchmark
  const userYoY = (() => {
    if (chartData.length < 2) return null;
    const last = chartData[chartData.length - 1];
    const prev = chartData[chartData.length - 2];
    if (!prev.revenue) return null;
    return Math.round(((last.revenue - prev.revenue) / prev.revenue) * 100);
  })();
  const peerYoY = 12; // TODO: derive from cohort benchmark data

  return (
    <div className="w-full h-full flex flex-col bg-transparent" aria-label={resolvedAriaLabel} aria-description="Estimated valuation trend based on historical revenue multiplied by industry revenue multiples">
      <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.valuation_trend.title" /></h3>
      <div className="text-succession-text-secondary text-xs mb-2"><FormattedMessage id="succession.valuation_trend.subtitle" /></div>
      <div className="flex-1 overflow-hidden">
        <ResponsiveContainer width="100%" height="100%" key={`vt-${chartData.length}-${chartData[chartData.length-1]?.valuation || 0}`}>
          <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <defs>
              <linearGradient id="valuationFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--succession-primary)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--succession-primary)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#203047" />
            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => currency(v)} width={72} />
            <Tooltip formatter={(value, name) => [currency(value), name === 'valuation' ? intl.formatMessage({ id: 'succession.valuation_trend.tooltip_valuation' }) : intl.formatMessage({ id: 'succession.valuation_trend.tooltip_revenue' })]} labelFormatter={(l) => `Year ${l}`} contentStyle={{ background: 'var(--succession-bg-primary)', border: '1px solid var(--succession-border)', color: 'var(--succession-text-primary)' }} />
            <Area type="monotone" dataKey="valuation" stroke="var(--succession-primary)" fillOpacity={1} fill="url(#valuationFill)" strokeWidth={2} />
            {/* Optional benchmark trend (placeholder) */}
            {userYoY != null && (
              <Line type="monotone" dataKey="valuation" data={chartData.map((d, i, arr) => ({ ...d, valuation: arr[0].valuation * Math.pow(1 + peerYoY / 100, i) }))} stroke="#7dd3fc" strokeDasharray="4 4" dot={false} name={intl.formatMessage({ id: 'succession.valuation_trend.peer_benchmark' })} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {userYoY != null && (
        <div className="text-xs text-succession-text-secondary mt-1"><FormattedMessage id="succession.valuation_trend.yoy_comparison" values={{ userYoY, peerYoY }} /></div>
      )}
    </div>
  );
};

export default ValuationTrend;




